import { FC, useState } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FormControl, InputLabel, Select, MenuItem, Grid, Button, Divider } from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { IDropdownResponse } from '../../models/util';

interface IDynamicFieldsFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  types: IDropdownResponse[];
  selectedType: string | number;
  setSelectedType: (val: string | number) => void;
  teams: IDropdownResponse[];
  selectedTeam: string | number;
  setSelectedTeam: (val: string | number) => void;
  hasAppliedFilters: boolean;
  setHasAppliedFilters: (val: boolean) => void;
}

export const DynamicFieldsFilters: FC<IDynamicFieldsFilters> = ({
  isLoading,
  applyFilters,
  types,
  selectedType,
  setSelectedType,
  teams,
  selectedTeam,
  setSelectedTeam,
  hasAppliedFilters,
  setHasAppliedFilters,
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });

  const noFilters = !selectedType && !selectedTeam;
  return (
    <>
      <Grid container spacing={1} alignItems="center" className={classes.searchWrapper}></Grid>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl
            classes={{ root: classes.formControlRoot }}
            fullWidth
            variant="outlined"
            size="small"
          >
            <InputLabel htmlFor="typeLabel" className={classes.ellipsis}>
              Type
            </InputLabel>
            <Select
              disabled={isLoading}
              name="type"
              labelId="typeLabel"
              id="type"
              value={selectedType}
              onChange={({ target: { value } }) => setSelectedType(value)}
            >
              {types &&
                types.map((type, index) => {
                  return (
                    <MenuItem key={`${index}`} value={type.value}>
                      {type.description}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl
            classes={{ root: classes.formControlRoot }}
            fullWidth
            variant="outlined"
            size="small"
          >
            <InputLabel htmlFor="teamLabel" className={classes.ellipsis}>
              Team
            </InputLabel>
            <Select
              disabled={isLoading}
              name="team"
              labelId="teamLabel"
              id="team"
              value={selectedTeam}
              onChange={({ target: { value } }) => setSelectedTeam(value)}
            >
              {teams &&
                teams.map((team, index) => {
                  return (
                    <MenuItem key={`${index}`} value={team.value}>
                      {team.description}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Button
            color="secondary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              if (noFilters) {
                setHasAppliedFilters(false);
                applyFilters();
              } else {
                setHasAppliedFilters(true);
                applyFilters();
              }
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              color="inherit"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSelectedTeam(teams[0].value);
                setSelectedType(types[0].value);
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    '@media only screen and (min-width: 900px)': {
      display: 'none',
    },
  },
  button: {
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',

    '@media only screen and (min-width: 900px)': {
      width: 'auto',
    },
  },
  resetButton: {
    '@media only screen and (min-width: 900px)': {
      marginLeft: '1rem',
    },
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex',
        }
      : {
          display: 'none',
          marginBottom: '1rem',
          '@media only screen and (min-width: 900px)': {
            display: 'flex',
          },
        },
  divider: {
    display: 'block',
    marginBottom: '1rem',
    '@media only screen and (min-width: 900px)': {
      display: 'none',
    },
  },
  gridItem: {
    padding: `3px 8px !important`,
    '@media only screen and (min-width: 900px)': {
      padding: `8px !important`,
    },
  },
  searchIcon: {
    cursor: 'pointer',
    color: '#9e9e9e',
  },
  ellipsis: {
    backgroundColor: 'white',
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible',
    },
  },
  formControlRoot: {
    marginTop: 0,
  },
  textFieldRoot: {
    marginBottom: 0,
  },
  searchWrapper: {
    marginBottom: '1rem',
    '@media only screen and (min-width: 900px)': {
      display: 'flex',
      marginBottom: '1rem',
    },
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: '1rem',
    },
  },
  searchButton: {
    minWidth: '8rem',
  },
  searchRoot: {
    marginTop: 0,
  },
}));
